import {useCallback, useRef} from 'react';
import {
  Root as TooltipRoot,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from '@radix-ui/react-tooltip';
import {GlyphCircle} from '@visx/glyph';
import {LinePath} from '@visx/shape';
import {format, parseISO} from 'date-fns';

import {Text} from '#component/base';
import * as css from '#component/partial/DeltaTrendChart/DeltaTrendChart.css';
import {getX, getY} from '#component/partial/DeltaTrendChart/DeltaTrendChart.util';
import {useOutsideClick} from '#hook/useOutsideClick';

import {GlyphProps} from './Glyph.types';

/**
 * `Glyph` is responsible for rendering the glyph that appears at each of the
 * data points on the `DeltaTrendChart`. It renders the circular glyph in the
 * appropriate style, as well as manages its own Tooltip.
 */
export const Glyph = ({
  deltaAge,
  formattedDeltaAge,
  isLast = false,
  isYounger = false,
  left = 0,
  onTooltipStateChange,
  isTooltipOpen,
  registeredDate,
  top = 0,
  xScale,
  yScale,
}: GlyphProps) => {
  const formattedRegisteredDate = format(parseISO(registeredDate), 'MMM d, Y');
  const triggerRef = useRef(null);

  const handleOpenTooltip = useCallback(() => {
    if (!isTooltipOpen) {
      onTooltipStateChange(true);
    }
  }, [isTooltipOpen, onTooltipStateChange]);

  const handleCloseTooltip = useCallback(() => {
    if (isTooltipOpen) {
      onTooltipStateChange(false);
    }
  }, [isTooltipOpen, onTooltipStateChange]);

  useOutsideClick(handleCloseTooltip, triggerRef);

  return (
    <TooltipRoot open={isTooltipOpen}>
      <LinePath
        className={css.hoveredIndicator}
        data={[
          {deltaAge, formattedDeltaAge, registeredDate},
          {deltaAge: yScale.domain()[0], formattedDeltaAge, registeredDate},
        ]}
        style={{opacity: isTooltipOpen ? 1 : 0}}
        x={d => (xScale(getX(d)) as number) ?? 0}
        y={d => (yScale(getY(d)) as number) ?? 0}
      />

      <g>
        <GlyphCircle
          className={css.glyph({isYounger, isLast})}
          left={left}
          size={isLast ? 60 : 80}
          top={top}
        />

        <foreignObject
          height={48}
          style={{transform: `translate(${left - 24}px, ${top - 24}px)`}}
          width={48}>
          <TooltipTrigger
            aria-label={`View details for ${formattedRegisteredDate}`}
            className={css.tooltipTrigger}
            onClick={handleOpenTooltip}
            onMouseEnter={handleOpenTooltip}
            onMouseLeave={handleCloseTooltip}
            ref={triggerRef}
          />
        </foreignObject>
      </g>

      <TooltipPortal>
        <TooltipContent
          className={css.tooltip({isYounger})}
          side={isYounger ? 'bottom' : 'top'}
          sideOffset={8}>
          <Text as="p" color="cotton" fontWeight="semiBold" textAlign="center">
            {`${isYounger ? '-' : '+'}${formattedDeltaAge.years}y ${formattedDeltaAge.months}m`}
          </Text>

          <Text as="p" color="cotton" variant="caption" textAlign="center">
            {formattedRegisteredDate}
          </Text>
        </TooltipContent>
      </TooltipPortal>
    </TooltipRoot>
  );
};
